import { useState, useEffect } from 'react';
import { Card } from '@/components/common/Card';
import { settingsService, Setting, CreateSettingDto, UpdateSettingDto, AzureEntraConfig } from '@/services/settings';
import toast from 'react-hot-toast';
import { Plus, Edit2, Trash2, Save, X, CheckCircle, XCircle, Cloud, Key, Eye, EyeOff, Shield, BarChart3, FileText, Clock3 } from 'lucide-react';

// Settings keys for Opportunities Posting
const POSTING_JOURNAL_TYPE_KEY = 'POSTING_JOURNAL_TYPE';
const POSTING_JOURNAL_TYPE_DEFAULT = 'CRMJ';
const DEFAULT_TAX_ID_KEY = 'DEFAULT_TAX_ID';
const DEFAULT_TAX_ID_DEFAULT = 'G1 Goods and Services Tax';
const SAGE_DIMENSIONS_REFRESH_TIME_KEY = 'SAGE_DIMENSIONS_REFRESH_TIME';
const SAGE_DIMENSIONS_REFRESH_TIME_DEFAULT = '22:00';

export function SettingsPage() {
  const [settings, setSettings] = useState<Setting[]>([]);
  const [azureEntraConfig, setAzureEntraConfig] = useState<AzureEntraConfig | null>(null);
  const [visibleFields, setVisibleFields] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingAzureConfig, setIsLoadingAzureConfig] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<UpdateSettingDto>({ value: '', description: '' });
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState<CreateSettingDto>({
    key: '',
    value: '',
    description: '',
    isSecret: false,
  });
  
  // Opportunities Posting settings
  const [postingJournalType, setPostingJournalType] = useState<string>(POSTING_JOURNAL_TYPE_DEFAULT);
  const [isEditingPostingJournalType, setIsEditingPostingJournalType] = useState(false);
  const [postingJournalTypeSetting, setPostingJournalTypeSetting] = useState<Setting | null>(null);
  
  const [defaultTaxId, setDefaultTaxId] = useState<string>(DEFAULT_TAX_ID_DEFAULT);
  const [isEditingDefaultTaxId, setIsEditingDefaultTaxId] = useState(false);
  const [defaultTaxIdSetting, setDefaultTaxIdSetting] = useState<Setting | null>(null);
  const [sageDimensionsRefreshTime, setSageDimensionsRefreshTime] = useState<string>(SAGE_DIMENSIONS_REFRESH_TIME_DEFAULT);
  const [isEditingSageDimensionsRefreshTime, setIsEditingSageDimensionsRefreshTime] = useState(false);
  const [sageDimensionsRefreshTimeSetting, setSageDimensionsRefreshTimeSetting] = useState<Setting | null>(null);

  useEffect(() => {
    loadSettings();
    loadAzureEntraConfig();
    loadOpportunitiesPostingSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setIsLoading(true);
      const data = await settingsService.getAllSettings();
      setSettings(data);
    } catch (error) {
      console.error('Failed to load settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setIsLoading(false);
    }
  };

  const loadAzureEntraConfig = async () => {
    try {
      setIsLoadingAzureConfig(true);
      const data = await settingsService.getAzureEntraConfig();
      setAzureEntraConfig(data);
    } catch (error) {
      console.error('Failed to load Azure Entra ID config:', error);
      // Don't show toast for this - it's not critical
    } finally {
      setIsLoadingAzureConfig(false);
    }
  };

  const loadOpportunitiesPostingSettings = async () => {
    try {
      // Load Posting Journal Type
      const journalTypeSetting = await settingsService.getSettingByKey(POSTING_JOURNAL_TYPE_KEY);
      if (journalTypeSetting) {
        setPostingJournalType(journalTypeSetting.value);
        setPostingJournalTypeSetting(journalTypeSetting);
      } else {
        setPostingJournalType(POSTING_JOURNAL_TYPE_DEFAULT);
        setPostingJournalTypeSetting(null);
      }
      
      // Load Default Tax ID
      const taxIdSetting = await settingsService.getSettingByKey(DEFAULT_TAX_ID_KEY);
      if (taxIdSetting) {
        setDefaultTaxId(taxIdSetting.value);
        setDefaultTaxIdSetting(taxIdSetting);
      } else {
        setDefaultTaxId(DEFAULT_TAX_ID_DEFAULT);
        setDefaultTaxIdSetting(null);
      }

      const sageRefreshTimeSetting = await settingsService.getSettingByKey(SAGE_DIMENSIONS_REFRESH_TIME_KEY);
      if (sageRefreshTimeSetting) {
        setSageDimensionsRefreshTime(sageRefreshTimeSetting.value);
        setSageDimensionsRefreshTimeSetting(sageRefreshTimeSetting);
      } else {
        setSageDimensionsRefreshTime(SAGE_DIMENSIONS_REFRESH_TIME_DEFAULT);
        setSageDimensionsRefreshTimeSetting(null);
      }
    } catch (error) {
      console.error('Failed to load opportunities posting settings:', error);
      setPostingJournalType(POSTING_JOURNAL_TYPE_DEFAULT);
      setDefaultTaxId(DEFAULT_TAX_ID_DEFAULT);
      setSageDimensionsRefreshTime(SAGE_DIMENSIONS_REFRESH_TIME_DEFAULT);
    }
  };

  const handleSavePostingJournalType = async () => {
    try {
      if (postingJournalTypeSetting) {
        // Update existing setting
        await settingsService.updateSetting(postingJournalTypeSetting.id, {
          value: postingJournalType,
          description: 'Journal type used for posting opportunities to Sage',
        });
      } else {
        // Create new setting
        await settingsService.createSetting({
          key: POSTING_JOURNAL_TYPE_KEY,
          value: postingJournalType,
          description: 'Journal type used for posting opportunities to Sage',
          isSecret: false,
        });
      }
      toast.success('Posting Journal Type saved successfully');
      setIsEditingPostingJournalType(false);
      loadOpportunitiesPostingSettings();
      loadSettings(); // Refresh all settings to keep in sync
    } catch (error) {
      console.error('Failed to save posting journal type:', error);
      toast.error('Failed to save posting journal type');
    }
  };

  const handleSaveDefaultTaxId = async () => {
    try {
      if (defaultTaxIdSetting) {
        // Update existing setting
        await settingsService.updateSetting(defaultTaxIdSetting.id, {
          value: defaultTaxId,
          description: 'Default Tax ID applied during validation for accounts 451000 and 857000',
        });
      } else {
        // Create new setting
        await settingsService.createSetting({
          key: DEFAULT_TAX_ID_KEY,
          value: defaultTaxId,
          description: 'Default Tax ID applied during validation for accounts 451000 and 857000',
          isSecret: false,
        });
      }
      toast.success('Default Tax ID saved successfully');
      setIsEditingDefaultTaxId(false);
      loadOpportunitiesPostingSettings();
      loadSettings(); // Refresh all settings to keep in sync
    } catch (error) {
      console.error('Failed to save default tax id:', error);
      toast.error('Failed to save default tax id');
    }
  };

  const handleSaveSageDimensionsRefreshTime = async () => {
    try {
      if (sageDimensionsRefreshTimeSetting) {
        await settingsService.updateSetting(sageDimensionsRefreshTimeSetting.id, {
          value: sageDimensionsRefreshTime,
          description: 'Daily time to refresh Sage dimension caches (24-hour server local time)',
        });
      } else {
        await settingsService.createSetting({
          key: SAGE_DIMENSIONS_REFRESH_TIME_KEY,
          value: sageDimensionsRefreshTime,
          description: 'Daily time to refresh Sage dimension caches (24-hour server local time)',
          isSecret: false,
        });
      }
      toast.success('Sage dimensions refresh time saved successfully');
      setIsEditingSageDimensionsRefreshTime(false);
      loadOpportunitiesPostingSettings();
      loadSettings();
    } catch (error) {
      console.error('Failed to save Sage dimensions refresh time:', error);
      toast.error('Failed to save Sage dimensions refresh time');
    }
  };

  const toggleFieldVisibility = (fieldName: string) => {
    setVisibleFields(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName]
    }));
  };

  const handleEdit = (setting: Setting) => {
    setEditingId(setting.id);
    setEditForm({
      value: setting.isSecret ? '' : setting.value,
      description: setting.description || '',
    });
  };

  const handleSaveEdit = async (id: string) => {
    try {
      await settingsService.updateSetting(id, editForm);
      toast.success('Setting updated successfully');
      setEditingId(null);
      loadSettings();
    } catch (error) {
      console.error('Failed to update setting:', error);
      toast.error('Failed to update setting');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({ value: '', description: '' });
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this setting?')) {
      return;
    }

    try {
      await settingsService.deleteSetting(id);
      toast.success('Setting deleted successfully');
      loadSettings();
    } catch (error) {
      console.error('Failed to delete setting:', error);
      toast.error('Failed to delete setting');
    }
  };

  const handleAdd = async () => {
    if (!addForm.key || !addForm.value) {
      toast.error('Key and value are required');
      return;
    }

    try {
      await settingsService.createSetting(addForm);
      toast.success('Setting created successfully');
      setShowAddForm(false);
      setAddForm({ key: '', value: '', description: '', isSecret: false });
      loadSettings();
    } catch (error) {
      console.error('Failed to create setting:', error);
      toast.error('Failed to create setting');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Settings</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Manage application settings (Administrator only)
          </p>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
        >
          <Plus className="h-4 w-4" />
          Add Setting
        </button>
      </div>

      {/* Add Setting Form */}
      {showAddForm && (
        <Card title="Add New Setting">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Key *
              </label>
              <input
                type="text"
                value={addForm.key}
                onChange={(e) => setAddForm({ ...addForm, key: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="e.g., AZURE_ENTRA_TENANT_ID"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Value *
              </label>
              <input
                type="text"
                value={addForm.value}
                onChange={(e) => setAddForm({ ...addForm, value: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description
              </label>
              <input
                type="text"
                value={addForm.description}
                onChange={(e) => setAddForm({ ...addForm, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isSecret"
                checked={addForm.isSecret}
                onChange={(e) => setAddForm({ ...addForm, isSecret: e.target.checked })}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="isSecret" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                This is a secret value (will be masked)
              </label>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleAdd}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
              >
                <Save className="h-4 w-4" />
                Save
              </button>
              <button
                onClick={() => setShowAddForm(false)}
                className="flex items-center gap-2 px-4 py-2 bg-gray-300 hover:bg-gray-400 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200 rounded-lg transition-colors"
              >
                <X className="h-4 w-4" />
                Cancel
              </button>
            </div>
          </div>
        </Card>
      )}

      {/* Azure Entra ID Configuration */}
      <Card title="Azure Entra ID Configuration">
        {isLoadingAzureConfig ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : azureEntraConfig ? (
          <div className="space-y-6">
            {/* Azure SSO Status */}
            <div className={`flex items-center gap-3 p-3 rounded-lg ${
              azureEntraConfig.ssoStatus.isConfigured 
                ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' 
                : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
            }`}>
              <Shield className={`h-5 w-5 ${
                azureEntraConfig.ssoStatus.isConfigured 
                  ? 'text-green-600 dark:text-green-400' 
                  : 'text-red-600 dark:text-red-400'
              }`} />
              <div>
                <p className={`text-sm font-medium ${
                  azureEntraConfig.ssoStatus.isConfigured 
                    ? 'text-green-800 dark:text-green-200' 
                    : 'text-red-800 dark:text-red-200'
                }`}>
                  Azure SSO Status
                </p>
                <p className={`text-xs ${
                  azureEntraConfig.ssoStatus.isConfigured 
                    ? 'text-green-600 dark:text-green-400' 
                    : 'text-red-600 dark:text-red-400'
                }`}>
                  {azureEntraConfig.ssoStatus.message}
                </p>
              </div>
            </div>

            {/* SSO Configuration Fields */}
            <div className="grid gap-4">
              {/* Tenant ID */}
              <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Key className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                    <span className="font-medium text-gray-900 dark:text-gray-100">Tenant ID</span>
                  </div>
                  {azureEntraConfig.tenantId.isConfigured ? (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-500" />
                  )}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <p className="flex-1 text-sm font-mono text-gray-600 dark:text-gray-400">
                    {visibleFields['tenantId'] ? azureEntraConfig.tenantId.value : azureEntraConfig.tenantId.maskedValue}
                  </p>
                  {azureEntraConfig.tenantId.isConfigured && (
                    <button
                      onClick={() => toggleFieldVisibility('tenantId')}
                      className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded transition-colors"
                      title={visibleFields['tenantId'] ? 'Hide value' : 'Show value'}
                    >
                      {visibleFields['tenantId'] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                  Source: {azureEntraConfig.tenantId.source}
                </p>
              </div>

              {/* Client ID */}
              <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Key className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                    <span className="font-medium text-gray-900 dark:text-gray-100">Client ID (Application ID)</span>
                  </div>
                  {azureEntraConfig.clientId.isConfigured ? (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-500" />
                  )}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <p className="flex-1 text-sm font-mono text-gray-600 dark:text-gray-400">
                    {visibleFields['clientId'] ? azureEntraConfig.clientId.value : azureEntraConfig.clientId.maskedValue}
                  </p>
                  {azureEntraConfig.clientId.isConfigured && (
                    <button
                      onClick={() => toggleFieldVisibility('clientId')}
                      className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded transition-colors"
                      title={visibleFields['clientId'] ? 'Hide value' : 'Show value'}
                    >
                      {visibleFields['clientId'] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                  Source: {azureEntraConfig.clientId.source}
                </p>
              </div>

              {/* Client Secret */}
              <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Key className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                    <span className="font-medium text-gray-900 dark:text-gray-100">Client Secret</span>
                  </div>
                  {azureEntraConfig.clientSecret.isConfigured ? (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-500" />
                  )}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <p className="flex-1 text-sm font-mono text-gray-600 dark:text-gray-400">
                    {azureEntraConfig.clientSecret.maskedValue}
                  </p>
                  {azureEntraConfig.clientSecret.isConfigured && (
                    <span className="text-xs text-gray-400 dark:text-gray-500 italic">
                      (Cannot be revealed for security)
                    </span>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                  Source: {azureEntraConfig.clientSecret.source}
                </p>
              </div>
            </div>

            {/* Azure Key Vault Group */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <div className="bg-gray-50 dark:bg-gray-800 px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                  <Cloud className="h-4 w-4" />
                  Azure Key Vault
                </h3>
              </div>
              <div className="p-4 space-y-4">
                {/* Vault Connection Status */}
                <div className={`flex items-center gap-3 p-3 rounded-lg ${
                  azureEntraConfig.vaultStatus.isConnected 
                    ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' 
                    : azureEntraConfig.keyVaultUrl.isConfigured
                      ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800'
                      : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                }`}>
                  <Cloud className={`h-5 w-5 ${
                    azureEntraConfig.vaultStatus.isConnected 
                      ? 'text-green-600 dark:text-green-400' 
                      : azureEntraConfig.keyVaultUrl.isConfigured
                        ? 'text-yellow-600 dark:text-yellow-400'
                        : 'text-red-600 dark:text-red-400'
                  }`} />
                  <div>
                    <p className={`text-sm font-medium ${
                      azureEntraConfig.vaultStatus.isConnected 
                        ? 'text-green-800 dark:text-green-200' 
                        : azureEntraConfig.keyVaultUrl.isConfigured
                          ? 'text-yellow-800 dark:text-yellow-200'
                          : 'text-red-800 dark:text-red-200'
                    }`}>
                      Azure Key Vault Status
                    </p>
                    <p className={`text-xs ${
                      azureEntraConfig.vaultStatus.isConnected 
                        ? 'text-green-600 dark:text-green-400' 
                        : azureEntraConfig.keyVaultUrl.isConfigured
                          ? 'text-yellow-600 dark:text-yellow-400'
                          : 'text-red-600 dark:text-red-400'
                    }`}>
                      {azureEntraConfig.vaultStatus.message}
                    </p>
                  </div>
                </div>

                {/* Key Vault URL */}
                <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Cloud className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                      <span className="font-medium text-gray-900 dark:text-gray-100">Key Vault URL</span>
                    </div>
                    {azureEntraConfig.keyVaultUrl.isConfigured ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <XCircle className="h-4 w-4 text-yellow-500" />
                    )}
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <p className="flex-1 text-sm font-mono text-gray-600 dark:text-gray-400">
                      {azureEntraConfig.keyVaultUrl.value}
                    </p>
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                    Source: {azureEntraConfig.keyVaultUrl.source}
                  </p>
                </div>
              </div>
            </div>

            {/* Application Insights Group */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <div className="bg-gray-50 dark:bg-gray-800 px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" />
                  Application Insights
                </h3>
              </div>
              <div className="p-4 space-y-4">
                {/* Application Insights Status */}
                <div className={`flex items-center gap-3 p-3 rounded-lg ${
                  azureEntraConfig.appInsights.status.isConnected 
                    ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' 
                    : azureEntraConfig.appInsights.status.isConfigured
                      ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800'
                      : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                }`}>
                  <BarChart3 className={`h-5 w-5 ${
                    azureEntraConfig.appInsights.status.isConnected 
                      ? 'text-green-600 dark:text-green-400' 
                      : azureEntraConfig.appInsights.status.isConfigured
                        ? 'text-yellow-600 dark:text-yellow-400'
                        : 'text-red-600 dark:text-red-400'
                  }`} />
                  <div>
                    <p className={`text-sm font-medium ${
                      azureEntraConfig.appInsights.status.isConnected 
                        ? 'text-green-800 dark:text-green-200' 
                        : azureEntraConfig.appInsights.status.isConfigured
                          ? 'text-yellow-800 dark:text-yellow-200'
                          : 'text-red-800 dark:text-red-200'
                    }`}>
                      Application Insights Status
                    </p>
                    <p className={`text-xs ${
                      azureEntraConfig.appInsights.status.isConnected 
                        ? 'text-green-600 dark:text-green-400' 
                        : azureEntraConfig.appInsights.status.isConfigured
                          ? 'text-yellow-600 dark:text-yellow-400'
                          : 'text-red-600 dark:text-red-400'
                    }`}>
                      {azureEntraConfig.appInsights.status.message}
                    </p>
                  </div>
                </div>

                {/* Instrumentation Key */}
                <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Key className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                      <span className="font-medium text-gray-900 dark:text-gray-100">Instrumentation Key</span>
                    </div>
                    {azureEntraConfig.appInsights.instrumentationKey.isConfigured ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <XCircle className="h-4 w-4 text-yellow-500" />
                    )}
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <p className="flex-1 text-sm font-mono text-gray-600 dark:text-gray-400">
                      {visibleFields['appInsightsKey'] ? azureEntraConfig.appInsights.instrumentationKey.value : azureEntraConfig.appInsights.instrumentationKey.maskedValue}
                    </p>
                    {azureEntraConfig.appInsights.instrumentationKey.isConfigured && (
                      <button
                        onClick={() => toggleFieldVisibility('appInsightsKey')}
                        className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded transition-colors"
                        title={visibleFields['appInsightsKey'] ? 'Hide value' : 'Show value'}
                      >
                        {visibleFields['appInsightsKey'] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                    Source: {azureEntraConfig.appInsights.instrumentationKey.source}
                  </p>
                </div>
              </div>
            </div>

            <p className="text-xs text-gray-500 dark:text-gray-400">
              These credentials are configured via environment variables. See the{' '}
              <a href="/docs/AZURE_ENTRA_ID_SSO.md" className="text-primary-600 hover:underline">
                Azure Entra ID SSO documentation
              </a>{' '}
              for setup instructions.
            </p>
          </div>
        ) : (
          <div className="text-center py-4 text-gray-500 dark:text-gray-400">
            Unable to load Azure Entra ID configuration
          </div>
        )}
      </Card>

      {/* Application Settings */}
      <Card title="Application Settings">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Opportunities Posting Group */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <div className="bg-gray-50 dark:bg-gray-800 px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Opportunities Posting
                </h3>
              </div>
              <div className="p-4 space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Configure settings for posting opportunities to Sage.
                </p>
                
                {/* Posting Journal Type */}
                <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                      <span className="font-medium text-gray-900 dark:text-gray-100">Posting Journal Type</span>
                    </div>
                    {!isEditingPostingJournalType ? (
                      <button
                        onClick={() => setIsEditingPostingJournalType(true)}
                        className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900 rounded transition-colors"
                        title="Edit"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                    ) : null}
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                    The journal type code used when posting journal entries to Sage (e.g., CRMJ)
                  </p>
                  
                  {isEditingPostingJournalType ? (
                    <div className="mt-3 space-y-3">
                      <input
                        type="text"
                        value={postingJournalType}
                        onChange={(e) => setPostingJournalType(e.target.value.toUpperCase())}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        placeholder="e.g., CRMJ"
                        maxLength={10}
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={handleSavePostingJournalType}
                          className="flex items-center gap-2 px-3 py-1 bg-primary-600 hover:bg-primary-700 text-white rounded transition-colors"
                        >
                          <Save className="h-4 w-4" />
                          Save
                        </button>
                        <button
                          onClick={() => {
                            setIsEditingPostingJournalType(false);
                            setPostingJournalType(postingJournalTypeSetting?.value || POSTING_JOURNAL_TYPE_DEFAULT);
                          }}
                          className="flex items-center gap-2 px-3 py-1 bg-gray-300 hover:bg-gray-400 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200 rounded transition-colors"
                        >
                          <X className="h-4 w-4" />
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="mt-2 text-sm font-mono text-gray-600 dark:text-gray-400">
                      {postingJournalType}
                    </p>
                  )}
                </div>
                
                {/* Default Tax ID */}
                <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                      <span className="font-medium text-gray-900 dark:text-gray-100">Default Tax ID</span>
                    </div>
                    {!isEditingDefaultTaxId ? (
                      <button
                        onClick={() => setIsEditingDefaultTaxId(true)}
                        className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900 rounded transition-colors"
                        title="Edit"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                    ) : null}
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                    Default Tax ID applied during validation for accounts 451000 and 857000
                  </p>
                  
                  {isEditingDefaultTaxId ? (
                    <div className="mt-3 space-y-3">
                      <input
                        type="text"
                        value={defaultTaxId}
                        onChange={(e) => setDefaultTaxId(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        placeholder="e.g., G1 Goods and Services Tax"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={handleSaveDefaultTaxId}
                          className="flex items-center gap-2 px-3 py-1 bg-primary-600 hover:bg-primary-700 text-white rounded transition-colors"
                        >
                          <Save className="h-4 w-4" />
                          Save
                        </button>
                        <button
                          onClick={() => {
                            setIsEditingDefaultTaxId(false);
                            setDefaultTaxId(defaultTaxIdSetting?.value || DEFAULT_TAX_ID_DEFAULT);
                          }}
                          className="flex items-center gap-2 px-3 py-1 bg-gray-300 hover:bg-gray-400 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200 rounded transition-colors"
                        >
                          <X className="h-4 w-4" />
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="mt-2 text-sm font-mono text-gray-600 dark:text-gray-400">
                      {defaultTaxId}
                    </p>
                  )}
                </div>

                <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Clock3 className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                      <span className="font-medium text-gray-900 dark:text-gray-100">Sage Dimensions Cache Refresh Time</span>
                    </div>
                    {!isEditingSageDimensionsRefreshTime ? (
                      <button
                        onClick={() => setIsEditingSageDimensionsRefreshTime(true)}
                        className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900 rounded transition-colors"
                        title="Edit"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                    ) : null}
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                    Daily time for the automatic Sage dimension cache refresh. Uses 24-hour server local time.
                  </p>

                  {isEditingSageDimensionsRefreshTime ? (
                    <div className="mt-3 space-y-3">
                      <input
                        type="time"
                        value={sageDimensionsRefreshTime}
                        onChange={(e) => setSageDimensionsRefreshTime(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={handleSaveSageDimensionsRefreshTime}
                          className="flex items-center gap-2 px-3 py-1 bg-primary-600 hover:bg-primary-700 text-white rounded transition-colors"
                        >
                          <Save className="h-4 w-4" />
                          Save
                        </button>
                        <button
                          onClick={() => {
                            setIsEditingSageDimensionsRefreshTime(false);
                            setSageDimensionsRefreshTime(sageDimensionsRefreshTimeSetting?.value || SAGE_DIMENSIONS_REFRESH_TIME_DEFAULT);
                          }}
                          className="flex items-center gap-2 px-3 py-1 bg-gray-300 hover:bg-gray-400 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200 rounded transition-colors"
                        >
                          <X className="h-4 w-4" />
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="mt-2 text-sm font-mono text-gray-600 dark:text-gray-400">
                      {sageDimensionsRefreshTime}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Other Settings */}
            {settings
              .filter((setting) => setting.key !== POSTING_JOURNAL_TYPE_KEY && setting.key !== DEFAULT_TAX_ID_KEY && setting.key !== SAGE_DIMENSIONS_REFRESH_TIME_KEY) // Filter out settings managed in dedicated sections
              .map((setting) => {
                const isEditing = editingId === setting.id;
                return (
                  <div key={setting.id} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                    {isEditing ? (
                      <div className="space-y-3">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-gray-100 mb-2">{setting.key}</p>
                          <input
                            type={setting.isSecret ? 'password' : 'text'}
                            value={editForm.value}
                            onChange={(e) => setEditForm({ ...editForm, value: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            placeholder={setting.isSecret ? 'Enter new value' : 'Value'}
                          />
                        </div>
                        <div>
                          <label className="text-sm text-gray-600 dark:text-gray-400 mb-1 block">Description</label>
                          <input
                            type="text"
                            value={editForm.description}
                            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          />
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleSaveEdit(setting.id)}
                            className="flex items-center gap-2 px-3 py-1 bg-primary-600 hover:bg-primary-700 text-white rounded transition-colors"
                          >
                            <Save className="h-4 w-4" />
                            Save
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="flex items-center gap-2 px-3 py-1 bg-gray-300 hover:bg-gray-400 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200 rounded transition-colors"
                          >
                            <X className="h-4 w-4" />
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex justify-between items-center">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900 dark:text-gray-100">{setting.key}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {setting.description || 'No description'}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-500 mt-1 font-mono">
                            {setting.value}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(setting)}
                            className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900 rounded transition-colors"
                            title="Edit"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(setting.id)}
                            className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900 rounded transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            {settings.filter((setting) => setting.key !== POSTING_JOURNAL_TYPE_KEY && setting.key !== DEFAULT_TAX_ID_KEY && setting.key !== SAGE_DIMENSIONS_REFRESH_TIME_KEY).length === 0 && (
              <p className="text-center text-gray-500 dark:text-gray-400 py-4">No additional application settings configured</p>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
